/* eslint-disable no-param-reassign */
import { v4 as uuid } from "uuid";
import { LayerSpecification, Map, MapSourceDataEvent } from "@iventis/mapbox-gl";
import { Basemap } from "../basemap";
import { Sitemap2dDataStore, Sitemap2d as Sitemap2dType } from "../../../../data-store/basemap/sitemap-2d-data-store";
import { layerScaleModifier } from "../../export-scale-helpers";
import { IventisMapboxLayerMetaDataSitemapLayer } from "../../test-helpers/mapbox-test-helper-types";
import { SitemapVersionLevel } from "../../../../types/store-schema";

export class Sitemap2d extends Basemap<Sitemap2dType, LayerSpecification["type"]> {
    /** 5 minutes buffer to refresh the sitemap signatures before they expire */
    private readonly refreshSignatureBuffer = 5 * 60 * 1000;

    private signatureExpiry: Date | null = null;

    private isRefreshingSignature = false;

    private refreshSitemapSignatureFn: (event: MapSourceDataEvent) => void;

    private loadingTimeout: NodeJS.Timeout | null = null;

    private readonly timeBetweenSourceLoadCheckMs = 500;

    private readonly metaData: IventisMapboxLayerMetaDataSitemapLayer;

    private sprite?: string;

    public readonly mapOrder: number | null;

    public layersAddedToMap = false;

    constructor(
        sitemapVersionLevel: SitemapVersionLevel,
        basemapDataStore: Sitemap2dDataStore,
        map: Map,
        private operations: { getAboveLayerId: (type: LayerSpecification["type"], orderValue: number) => string },
        layerScale?: number
    ) {
        super(sitemapVersionLevel.id, basemapDataStore, map);
        this.mapOrder = sitemapVersionLevel.mapOrder;
        this.metaData = {
            sitemapName: sitemapVersionLevel.sitemapName,
            sitemapVersionName: sitemapVersionLevel.sitemapVersionName,
            levelIndex: sitemapVersionLevel.levelIndex,
            type: "sitemap",
        };
        this.addToMap(layerScale);
    }

    public getLayers() {
        return this.layers;
    }

    protected async addToMap(layerScale?: number): Promise<void> {
        const { layers, sources, expiry, sprite } = await this.basemapDataStore.getBasemap(this.id);
        this.sprite = sprite;

        await this.waitForMapToBeReadyToLoadSitemap();

        // As we are awaiting above, before we continue check the sitemap hasn't been removed
        if (this.removed) {
            return;
        }

        Object.entries(sources).forEach(([sourceId, source]) => {
            if (!this.map.getSource(sourceId)) {
                this.map.addSource(sourceId, source);
                this.sourceIds.push(sourceId);
            }
        });

        this.signatureExpiry = new Date(expiry);
        this.refreshSitemapSignatureFn = this.refreshSitemapSignature.bind(this);

        // On every time the a source request is made
        this.map.on("sourcedataloading", this.refreshSitemapSignatureFn);

        if (layerScale) {
            layerScaleModifier(layers, layerScale);
        }

        await this.loadSpriteImages(sprite);

        if (this.removed) {
            return;
        }

        layers.forEach((layer) => {
            // Need to generate a new unique id for every sitemap layer
            // In production there are cases of multiple sitemap layers having the same id which causes issues
            layer.id = uuid();
            layer.metadata = this.metaData;
            if (!this.map.getLayer(layer.id)) {
                this.map.addLayer(layer, this.operations.getAboveLayerId(layer.type, this.mapOrder));
                this.layers.push({ id: layer.id, type: layer.type });
            }
        });

        this.layersAddedToMap = true;

        this.map.on("sourcedata", (event) => {
            if (!this.loaded && this.sourceIds.some((sourceId) => event.sourceId === sourceId)) {
                if (this.loadingTimeout) {
                    clearTimeout(this.loadingTimeout);
                }
                this.loadingTimeout = setTimeout(() => {
                    this.loaded = true;
                }, this.timeBetweenSourceLoadCheckMs);
            }
        });
    }

    public async reloadSpriteImages() {
        if (this.removed) {
            return;
        }

        await this.loadSpriteImages(this.sprite);
    }

    private async refreshSitemapSignature(event: MapSourceDataEvent) {
        // Check it is this source
        if (this.sourceIds.some((sourceId) => event.sourceId === sourceId)) {
            // Is not refreshing the current and the current source is expired
            if (!this.isRefreshingSignature && this.signatureExpiry.getTime() - this.refreshSignatureBuffer < new Date().getTime()) {
                this.isRefreshingSignature = true;
                // Get a new sitemap asset with an update signature
                this.basemapDataStore.bustBasemapById(this.id);
                const sitemap = await this.basemapDataStore.getBasemap(this.id);

                // Set new expiry time and set new timeout for next signature refresh
                this.signatureExpiry = new Date(sitemap.expiry);

                Object.entries(sitemap.sources).forEach(([sourceId, source]) => {
                    // Update the source url with the updated signature
                    const mapboxSource = this.map.getSource(sourceId);
                    if (mapboxSource?.type === "vector" && source.type === "vector") {
                        if (source.tiles != null) {
                            mapboxSource.setTiles(source.tiles);
                        }
                        if (source.url != null) {
                            mapboxSource.setUrl(source.url);
                        }
                    }
                });

                this.isRefreshingSignature = false;
            }
        }
    }

    private async loadSpriteImages(sprite?: string) {
        if (!sprite || !sprite.startsWith("https://")) {
            return;
        }

        let spriteMetadata: Record<string, { x: number; y: number; width: number; height: number; pixelRatio?: number; sdf?: boolean }>;
        try {
            const spriteMetadataResponse = await fetch(`${sprite}.json`);
            if (!spriteMetadataResponse.ok) {
                console.warn(`Failed to fetch sprite metadata '${sprite}.json', response status: ${spriteMetadataResponse.status}`);
                return;
            }
            spriteMetadata = await spriteMetadataResponse.json();
        } catch (error) {
            console.warn(`Failed to fetch sprite metadata '${sprite}.json'`, error);
            return;
        }

        let spriteImage: HTMLImageElement | ImageBitmap | ImageData;
        try {
            spriteImage = await new Promise<HTMLImageElement | ImageBitmap | ImageData>((resolve, reject) => {
                this.map.loadImage(`${sprite}.png`, (error, image) => {
                    if (error) {
                        reject(error);
                        return;
                    }
                    if (image == null) {
                        reject(new Error(`Failed to load sprite image '${sprite}'`));
                        return;
                    }
                    resolve(image);
                });
            });
        } catch (error) {
            console.warn(`Failed to load sprite image '${sprite}.png'`, error);
            return;
        }

        await Promise.all(
            Object.entries(spriteMetadata).map(async ([id, metadata]) => {
                if (this.map.hasImage(id)) {
                    return;
                }

                const canvas = document.createElement("canvas");
                canvas.width = metadata.width;
                canvas.height = metadata.height;
                const context = canvas.getContext("2d");
                if (context == null) {
                    return;
                }

                if (this.isImageData(spriteImage)) {
                    context.putImageData(spriteImage, -metadata.x, -metadata.y);
                } else {
                    context.drawImage(spriteImage, metadata.x, metadata.y, metadata.width, metadata.height, 0, 0, metadata.width, metadata.height);
                }
                this.map.addImage(id, context.getImageData(0, 0, metadata.width, metadata.height), {
                    pixelRatio: metadata.pixelRatio ?? 1,
                    sdf: metadata.sdf ?? false,
                });
            })
        );
    }

    private isImageData(image: HTMLImageElement | ImageBitmap | ImageData): image is ImageData {
        return typeof ImageData !== "undefined" && image instanceof ImageData;
    }

    public remove(): void {
        super.remove();
        this.map.off("sourcedataloading", this.refreshSitemapSignatureFn);
        if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout);
        }
    }
}
